import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ChatRequest, ChatResponse, ChatSession, ChatMessage, ChatStreamChunk, AssessmentProgressDto } from '../models/chat.models';
import { environment } from '../../environments/environment';
import { CalAngularService } from '@cvx/cal-angular';

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private readonly STORAGE_KEY = 'nora_chat_session_id';

  private apiUrl: string;
  private currentSessionId$ = new BehaviorSubject<string | null>(
    localStorage.getItem('nora_chat_session_id')
  );
  private isTyping$ = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private calService: CalAngularService,
    private ngZone: NgZone
  ) {
    this.apiUrl = environment.apiUrl + '/chat';

    // Sync session ID changes to localStorage so the conversation survives page refresh.
    this.currentSessionId$.subscribe(id => {
      if (id) {
        localStorage.setItem(this.STORAGE_KEY, id);
      } else {
        localStorage.removeItem(this.STORAGE_KEY);
      }
    });
  }

  get currentSessionId(): Observable<string | null> {
    return this.currentSessionId$.asObservable();
  }

  get isTyping(): Observable<boolean> {
    return this.isTyping$.asObservable();
  }

  /**
   * Create a new chat session
   */
  createSession(): Observable<ChatSession> {
    return this.http.post<ChatSession>(`${this.apiUrl}/sessions`, {}).pipe(
      tap(session => this.currentSessionId$.next(session.id))
    );
  }

  /**
   * Send a message to the AI chatbot
   */
  sendMessage(
    query: string,
    sessionId?: string,
    knowledgeSources?: string[],
    trackedBusinessApplicationId?: number
  ): Observable<ChatResponse> {
    const sid = sessionId || this.currentSessionId$.value || this.generateSessionId();
    
    if (!this.currentSessionId$.value) {
      this.currentSessionId$.next(sid);
    }

    const request: ChatRequest = {
      query,
      sessionId: sid,
      knowledgeSources,
      trackedBusinessApplicationId,
    };

    this.isTyping$.next(true);

    return this.http.post<ChatResponse>(`${this.apiUrl}/message`, request).pipe(
      tap({
        next: () => this.isTyping$.next(false),
        error: () => this.isTyping$.next(false)
      })
    );
  }

  /**
   * Send a message and stream the AI response via SSE.
   * Uses fetch() instead of HttpClient because Angular's HttpClient
   * does not support streaming text/event-stream responses.
   */
  sendMessageStream(
    query: string,
    sessionId?: string,
    knowledgeSources?: string[],
    trackedBusinessApplicationId?: number
  ): Observable<ChatStreamChunk> {
    const sid = sessionId || this.currentSessionId$.value || this.generateSessionId();

    if (!this.currentSessionId$.value) {
      this.currentSessionId$.next(sid);
    }

    const request: ChatRequest = {
      query,
      sessionId: sid,
      knowledgeSources,
      trackedBusinessApplicationId,
    };

    this.isTyping$.next(true);

    const subject = new Subject<ChatStreamChunk>();

    this.getAccessToken().then(async (token) => {
      try {
        const response = await fetch(`${this.apiUrl}/message/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify(request)
        });

        if (!response.ok || !response.body) {
          throw new Error(`Stream request failed: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let chunkCount = 0;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Parse SSE frames: "data: {...}\n\n"
          const lines = buffer.split('\n\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            const dataLine = line.trim();
            if (dataLine.startsWith('data: ')) {
              const jsonStr = dataLine.substring(6);
              try {
                const chunk: ChatStreamChunk = JSON.parse(jsonStr);
                chunkCount++;
                this.ngZone.run(() => subject.next(chunk));

                if (chunk.isComplete) {
                  this.ngZone.run(() => this.isTyping$.next(false));
                }
              } catch {
                // Skip malformed JSON frames
              }
            }
          }
        }

        this.ngZone.run(() => subject.complete());
      } catch (err) {
        this.ngZone.run(() => {
          this.isTyping$.next(false);
          subject.error(err);
        });
      }
    });

    return subject.asObservable();
  }

  /**
   * Get all chat sessions for the current user
   */
  getSessions(): Observable<ChatSession[]> {
    return this.http.get<ChatSession[]>(`${this.apiUrl}/sessions`);
  }

  /**
   * Poll for live assessment progress started by this chat session.
   * Returns null (HTTP 204) when no active job is tracked.
   */
  getAssessmentProgress(sessionId: string, sinceLogId: number = 0): Observable<AssessmentProgressDto | null> {
    return this.http.get<AssessmentProgressDto | null>(
      `${this.apiUrl}/session/${sessionId}/assessment-progress?sinceLogId=${sinceLogId}`
    );
  }

  /**
   * Get message history for a specific session
   */
  getSessionHistory(sessionId: string): Observable<ChatMessage[]> {
    return this.http.get<ChatMessage[]>(`${this.apiUrl}/sessions/${sessionId}/messages`);
  }

  /**
   * Set the current active session
   */
  setCurrentSession(sessionId: string | null): void {
    this.currentSessionId$.next(sessionId);
  }

  /**
   * Start a new conversation (generates new session ID)
   */
  startNewConversation(): void {
    const newSessionId = this.generateSessionId();
    this.currentSessionId$.next(newSessionId);
  }

  /**
   * Get an access token from CAL/MSAL for use with fetch() (SSE streaming).
   */
  private async getAccessToken(): Promise<string | null> {
    const scopes = [environment.clientId + '/user_impersonation'];
    try {
      const result = await this.calService.getAADToken(scopes, false).toPromise();
      if (typeof result === 'string') return result;
      if (result && typeof result === 'object' && 'accessToken' in result) {
        return (result as any).accessToken;
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Generate a cryptographically secure session ID (UUID v4)
   */
  private generateSessionId(): string {
    return crypto.randomUUID();
  }
}
